<template>
  <div class="transaction-immersive-page">
    <!-- Header Bar -->
    <header class="preview-header d-flex align-items-center justify-content-between px-2 py-75 bg-white border-bottom shadow-sm">
      <!-- Title (Left aligned) -->
      <div class="mr-2">
        <h4 class="mb-0 page-title">
          Daftar Transaksi
        </h4>
      </div>

      <!-- Filters & Actions -->
      <div class="d-flex align-items-end flex-wrap">
        <!-- Date Range Filter -->
        <div
          class="mr-1"
          style="min-width: 180px;"
        >
          <label class="filter-label mb-25 d-block">
            Periode
          </label>
          <v-select
            v-model="selectedDateRange"
            :options="dateRangeOptions"
            label="label"
            :reduce="opt => opt.value"
            :clearable="false"
            :searchable="false"
            :transition="''"
            @input="handleDateRangeChange"
          />
        </div>

        <!-- Custom Date Range pickers (shown only on Custom) -->
        <div
          v-if="selectedDateRange === 'custom'"
          class="d-flex align-items-end mr-1"
        >
          <div
            class="mr-1"
            style="min-width: 175px;"
          >
            <label class="filter-label mb-25 d-block">
              Dari tanggal
            </label>
            <b-form-datepicker
              v-model="customStartDate"
              placeholder="Pilih tanggal"
              :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit' }"
              :disabled="isLoading"
              @input="handleCustomDateChange"
            />
          </div>
          <div style="min-width: 175px;">
            <label class="filter-label mb-25 d-block">
              Hingga tanggal
            </label>
            <b-form-datepicker
              v-model="customEndDate"
              placeholder="Pilih tanggal"
              :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit' }"
              :disabled="isLoading"
              @input="handleCustomDateChange"
            />
          </div>
        </div>

        <!-- Filter Status -->
        <div
          class="mr-1"
          style="min-width: 200px;"
        >
          <label class="filter-label mb-25 d-block">
            Status Transaksi
          </label>
          <v-select
            v-model="selectedStatus"
            :options="statusOptions"
            label="label"
            :reduce="opt => opt.value"
            placeholder="Semua Status"
            :clearable="true"
            :searchable="false"
            :transition="''"
            @input="handleStatusChange"
          />
        </div>

        <!-- Export File Button -->
        <div>
          <b-button
            variant="primary"
            :disabled="isLoading || transactions.length === 0"
            @click="exportExcel"
          >
            <feather-icon
              icon="DownloadIcon"
              size="16"
              class="mr-50"
            />
            Export File
          </b-button>
        </div>
      </div>
    </header>

    <!-- Spreadsheet Area (only scrollable area) -->
    <main class="preview-spreadsheet-container">
      <SpreadsheetPreview
        :sheet-data="sheetData"
        :loading="isLoading"
        :loading-text="loadingText"
      />
    </main>
  </div>
</template>

<script>
import {
  BButton,
  BFormDatepicker,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import * as XLSX from 'xlsx'
import moment from 'moment'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import SpreadsheetPreview from '@/components/SpreadsheetPreview.vue'

export default {
  name: 'TransactionPreview',
  components: {
    BButton,
    BFormDatepicker,
    vSelect,
    SpreadsheetPreview,
  },
  data() {
    return {
      isLoading: false,
      isFetching: false,
      loadingText: 'Memuat transaksi...',
      transactions: [],
      selectedDateRange: 'this_month',
      customStartDate: null,
      customEndDate: null,
      currentStartDate: '',
      currentEndDate: '',
      dateRangeOptions: [
        { label: 'Bulan ini', value: 'this_month' },
        { label: 'Bulan lalu', value: 'last_month' },
        { label: 'Custom', value: 'custom' },
      ],
      selectedStatus: null,
      wasDark: false,
      statusOptions: [
        { label: 'Semua Status', value: null },
        { label: 'Selesai', value: 'delivered' },
        { label: 'Diproses', value: 'accepted,pickedup' },
        { label: 'Batal', value: 'cancelled,rejected' },
      ],
      sheetData: {},
    }
  },
  mounted() {
    // Force light mode only for this page
    this.wasDark = document.body.classList.contains('dark-layout')
    if (this.wasDark) {
      document.body.classList.remove('dark-layout')
    }
    if (document.body.classList.contains('semi-dark-layout')) {
      document.body.classList.remove('semi-dark-layout')
    }
    document.documentElement.style.colorScheme = 'light'
    document.documentElement.style.overscrollBehavior = 'none'
    document.body.style.overscrollBehavior = 'none'

    // Initialize dates and status from route query params
    this.initDatesFromQuery()
    this.initEmptySheet()
    this.fetchAllTransactions()
  },
  beforeDestroy() {
    // Restore previous theme if user was in dark mode
    if (this.wasDark) {
      document.body.classList.add('dark-layout')
    }
    document.documentElement.style.colorScheme = ''
    document.documentElement.style.overscrollBehavior = ''
    document.body.style.overscrollBehavior = ''
  },
  methods: {
    initDatesFromQuery() {
      const q = this.$route.query
      const thisMonthStart = moment().startOf('month').format('YYYY-MM-DD')
      const thisMonthEnd = moment().endOf('month').format('YYYY-MM-DD')
      const lastMonthStart = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
      const lastMonthEnd = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')

      if (q.date_range) {
        this.selectedDateRange = q.date_range
        if (q.date_range === 'this_month') {
          this.currentStartDate = thisMonthStart
          this.currentEndDate = thisMonthEnd
        } else if (q.date_range === 'last_month') {
          this.currentStartDate = lastMonthStart
          this.currentEndDate = lastMonthEnd
        } else if (q.date_range === 'custom') {
          this.currentStartDate = q.start_date || thisMonthStart
          this.currentEndDate = q.end_date || thisMonthEnd
          this.customStartDate = this.currentStartDate
          this.customEndDate = this.currentEndDate
        }
      } else if (q.start_date && q.end_date) {
        if (q.start_date === thisMonthStart && q.end_date === thisMonthEnd) {
          this.selectedDateRange = 'this_month'
          this.currentStartDate = thisMonthStart
          this.currentEndDate = thisMonthEnd
        } else if (q.start_date === lastMonthStart && q.end_date === lastMonthEnd) {
          this.selectedDateRange = 'last_month'
          this.currentStartDate = lastMonthStart
          this.currentEndDate = lastMonthEnd
        } else {
          this.selectedDateRange = 'custom'
          this.currentStartDate = q.start_date
          this.currentEndDate = q.end_date
          this.customStartDate = q.start_date
          this.customEndDate = q.end_date
        }
      } else {
        this.selectedDateRange = 'this_month'
        this.currentStartDate = thisMonthStart
        this.currentEndDate = thisMonthEnd
      }

      if (q.status) {
        this.selectedStatus = q.status
      }
    },

    handleDateRangeChange(val) {
      const thisMonthStart = moment().startOf('month').format('YYYY-MM-DD')
      const thisMonthEnd = moment().endOf('month').format('YYYY-MM-DD')
      const lastMonthStart = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
      const lastMonthEnd = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')

      if (val === 'this_month') {
        this.currentStartDate = thisMonthStart
        this.currentEndDate = thisMonthEnd
      } else if (val === 'last_month') {
        this.currentStartDate = lastMonthStart
        this.currentEndDate = lastMonthEnd
      } else if (val === 'custom') {
        if (!this.customStartDate) this.customStartDate = this.currentStartDate || thisMonthStart
        if (!this.customEndDate) this.customEndDate = this.currentEndDate || thisMonthEnd
        this.currentStartDate = this.customStartDate
        this.currentEndDate = this.customEndDate
      }

      this.syncQueryParams()
      this.fetchAllTransactions()
    },

    handleCustomDateChange() {
      if (!this.customStartDate || !this.customEndDate) return
      if (this.customStartDate === this.currentStartDate && this.customEndDate === this.currentEndDate) return

      this.currentStartDate = this.customStartDate
      this.currentEndDate = this.customEndDate
      this.syncQueryParams()
      this.fetchAllTransactions()
    },

    handleStatusChange() {
      this.syncQueryParams()
      this.fetchAllTransactions()
    },

    syncQueryParams() {
      const query = {
        date_range: this.selectedDateRange,
        start_date: this.currentStartDate,
        end_date: this.currentEndDate,
      }
      if (this.selectedStatus) {
        query.status = this.selectedStatus
      }

      const currentQuery = this.$route.query
      const isSame = Object.keys(query).length === Object.keys(currentQuery).length
        && Object.keys(query).every(k => query[k] === currentQuery[k])

      if (!isSame) {
        this.$router.replace({ query }).catch(() => {})
      }
    },

    initEmptySheet() {
      this.sheetData = {
        name: 'Transactions',
        freeze: 'B2',
        styles: [
          {
            bgcolor: '#f8f9fa',
            font: { bold: true },
            align: 'center',
            valign: 'middle',
          },
        ],
        cols: {
          0: { width: 60 },
          1: { width: 180 },
          2: { width: 180 },
          3: { width: 110 },
          4: { width: 200 },
          5: { width: 110 },
          6: { width: 140 },
          7: { width: 160 },
          8: { width: 120 },
          9: { width: 120 },
          10: { width: 140 },
          11: { width: 110 },
        },
        rows: {
          0: {
            height: 56, // Twice standard 28px height
            cells: {
              0: { text: 'No.', style: 0 },
              1: { text: 'Customer Name', style: 0 },
              2: { text: 'Merchant', style: 0 },
              3: { text: 'Invoice Date', style: 0 },
              4: { text: 'Invoice Number', style: 0 },
              5: { text: 'Period', style: 0 },
              6: { text: 'Transaction Status', style: 0 },
              7: { text: 'Total Sales Amount', style: 0 },
              8: { text: 'Delivery Fee', style: 0 },
              9: { text: 'Insurance Fee', style: 0 },
              10: { text: 'Billed Total', style: 0 },
              11: { text: 'Source', style: 0 },
            },
          },
        },
      }
    },

    mapStatus(rawStatus) {
      const s = String(rawStatus || '').toLowerCase()
      if (s === 'delivered') return 'Selesai'
      if (['cancelled', 'rejected'].includes(s)) return 'Batal'
      if (['accepted', 'confirmed', 'pickedup'].includes(s)) return 'Diproses'
      return rawStatus || '-'
    },

    async fetchAllTransactions() {
      if (this.isFetching) return
      this.isFetching = true
      this.isLoading = true
      this.loadingText = 'Memuat transaksi...'
      this.transactions = []

      let page = 1
      let lastPage = 1
      const limit = 100
      const accumulated = []

      try {
        do {
          const params = {
            page,
            limit,
            start_date: this.currentStartDate,
            end_date: this.currentEndDate,
            has_paid: true,
          }

          if (this.selectedStatus) {
            params.status = this.selectedStatus
          }

          // eslint-disable-next-line no-await-in-loop
          const response = await this.$http.get('/v3/admin/transactions/preview', { params })
          const resData = response.data || {}
          const items = resData.data || []
          accumulated.push(...items)

          const meta = resData.meta || {}
          lastPage = meta.last_page || 1
          page += 1
        } while (page <= lastPage)

        this.transactions = accumulated
        this.renderSpreadsheetData(accumulated)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Gagal memuat data transaksi',
            text: error?.response?.data?.message || error.message || 'Terjadi kesalahan sistem',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.isLoading = false
        this.isFetching = false
      }
    },

    renderSpreadsheetData(data) {
      // Style index dictionary:
      // 0: Header style (left-aligned)
      // 1: Normal cell (left-aligned)
      // 2: Normal amount (right-aligned)
      // 3: Batal row normal text (Red background + red text, left-aligned)
      // 4: Batal row amount (Red background + red text + right-aligned)
      // 5: Sum label (Bold + right-aligned)
      // 6: Sum formula value (Bold + right-aligned)
      // 7: Normal sequence number / date / center text (center-aligned)
      // 8: Batal row sequence number / center text (Red background + red text, center-aligned)
      const styles = [
        {
          bgcolor: '#f8f9fa',
          font: { bold: true },
          align: 'center',
          valign: 'middle',
        },
        { align: 'left', valign: 'middle' },
        { align: 'right', valign: 'middle' },
        {
          bgcolor: '#fde8e8', color: '#9b1c1c', align: 'left', valign: 'middle',
        },
        {
          bgcolor: '#fde8e8', color: '#9b1c1c', align: 'right', valign: 'middle',
        },
        { font: { bold: true }, align: 'right', valign: 'middle' },
        { font: { bold: true }, align: 'right', valign: 'middle' },
        { align: 'center', valign: 'middle' },
        {
          bgcolor: '#fde8e8', color: '#9b1c1c', align: 'center', valign: 'middle',
        },
      ]

      const totalRowsCount = data.length
      const rows = {
        len: Math.max(totalRowsCount + 20, 100),
        0: {
          height: 56, // Twice standard 28px height
          cells: {
            0: { text: 'No.', style: 0 },
            1: { text: 'Customer Name', style: 0 },
            2: { text: 'Merchant', style: 0 },
            3: { text: 'Invoice Date', style: 0 },
            4: { text: 'Invoice Number', style: 0 },
            5: { text: 'Period', style: 0 },
            6: { text: 'Transaction Status', style: 0 },
            7: { text: 'Total Sales Amount', style: 0 },
            8: { text: 'Delivery Fee', style: 0 },
            9: { text: 'Insurance Fee', style: 0 },
            10: { text: 'Billed Total', style: 0 },
            11: { text: 'Source', style: 0 },
          },
        },
      }

      data.forEach((item, index) => {
        const rowIndex = index + 1
        const humanStatus = this.mapStatus(item.status)
        const isBatal = humanStatus === 'Batal'
        const formattedNo = (index + 1).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')

        const textStyle = isBatal ? 3 : 1
        const amountStyle = isBatal ? 4 : 2
        const centerStyle = isBatal ? 8 : 7

        const invoiceDate = item.created_at ? moment(item.created_at).format('YYYY/MM/DD') : '-'
        const period = item.created_at ? moment(item.created_at).locale('en').format('MMMM') : '-'
        const insuranceFee = item.insurance_fee ? item.insurance_fee : ''

        rows[rowIndex] = {
          cells: {
            0: { text: formattedNo, style: centerStyle },
            1: { text: item.customer_name || '-', style: textStyle },
            2: { text: item.merchant_name || '-', style: textStyle },
            3: { text: invoiceDate, style: centerStyle },
            4: { text: item.invoice_number || '-', style: centerStyle },
            5: { text: period, style: centerStyle },
            6: { text: humanStatus, style: centerStyle },
            7: { text: item.total !== undefined ? item.total : 0, style: amountStyle },
            8: { text: item.delivery_fee !== undefined ? item.delivery_fee : 0, style: amountStyle },
            9: { text: insuranceFee, style: amountStyle },
            10: { text: item.billed_total !== undefined ? item.billed_total : 0, style: amountStyle },
            11: { text: item.source || '-', style: centerStyle },
          },
        }
      })

      // Add bottom row: Sum formula for Total Sales Amount (Column H) and Billed Total (Column K)
      const sumRowIndex = totalRowsCount + 1

      if (totalRowsCount > 0) {
        // Excel 1-based indexing: Row 1 is header, data is H2 to H(totalRowsCount + 1), K2 to K(totalRowsCount + 1)
        const formulaSalesSum = `=SUM(H2:H${totalRowsCount + 1})`
        const formulaBilledSum = `=SUM(K2:K${totalRowsCount + 1})`
        rows[sumRowIndex] = {
          cells: {
            0: { text: '', style: 1 },
            1: { text: '', style: 1 },
            2: { text: '', style: 1 },
            3: { text: '', style: 1 },
            4: { text: '', style: 1 },
            5: { text: '', style: 1 },
            6: { text: '', style: 1 },
            7: { text: formulaSalesSum, style: 6 },
            8: { text: '', style: 1 },
            9: { text: '', style: 1 },
            10: { text: formulaBilledSum, style: 6 },
            11: { text: '', style: 1 },
          },
        }
      }

      // Detect consecutive order_group_id merges for Billed Total (Col K) and Source (Col L)
      const merges = []
      let groupStart = 0

      for (let i = 0; i < data.length; i += 1) {
        const currentGroupId = data[i].order_group_id
        const nextGroupId = i + 1 < data.length ? data[i + 1].order_group_id : null

        if (!currentGroupId || currentGroupId !== nextGroupId) {
          const groupEnd = i
          if (currentGroupId && groupEnd > groupStart) {
            // Excel row is 1-based. Row 1 is header, data[0] is Row 2.
            const startExcelRow = groupStart + 2
            const endExcelRow = groupEnd + 2
            merges.push(`K${startExcelRow}:K${endExcelRow}`)
            merges.push(`L${startExcelRow}:L${endExcelRow}`)

            // Set cell.merge on the top cell and clear secondary cells
            const startRowIndex = groupStart + 1
            const spanRows = groupEnd - groupStart

            if (rows[startRowIndex] && rows[startRowIndex].cells) {
              if (rows[startRowIndex].cells[10]) {
                rows[startRowIndex].cells[10].merge = [spanRows, 0]
              }
              if (rows[startRowIndex].cells[11]) {
                rows[startRowIndex].cells[11].merge = [spanRows, 0]
              }
            }

            for (let r = groupStart + 1; r <= groupEnd; r += 1) {
              const secondaryRowIndex = r + 1
              if (rows[secondaryRowIndex] && rows[secondaryRowIndex].cells) {
                delete rows[secondaryRowIndex].cells[10]
                delete rows[secondaryRowIndex].cells[11]
              }
            }
          }
          groupStart = i + 1
        }
      }

      this.sheetData = {
        name: 'Transactions',
        freeze: 'B2',
        styles,
        merges,
        cols: {
          0: { width: 60 },
          1: { width: 180 },
          2: { width: 180 },
          3: { width: 110 },
          4: { width: 200 },
          5: { width: 110 },
          6: { width: 140 },
          7: { width: 160 },
          8: { width: 120 },
          9: { width: 120 },
          10: { width: 140 },
          11: { width: 110 },
        },
        rows,
      }
    },

    exportExcel() {
      if (!this.transactions.length) return

      const headers = [
        'No.',
        'Customer Name',
        'Merchant',
        'Invoice Date',
        'Invoice Number',
        'Period',
        'Transaction Status',
        'Total Sales Amount',
        'Delivery Fee',
        'Insurance Fee',
        'Billed Total',
        'Source',
      ]

      const exportRows = [headers]

      let totalSalesSum = 0
      let totalBilledSum = 0
      let lastGroupId = null

      this.transactions.forEach((item, index) => {
        const totalNum = Number(item.total) || 0
        totalSalesSum += totalNum

        const deliveryFee = Number(item.delivery_fee) || 0
        const insuranceFee = item.insurance_fee ? Number(item.insurance_fee) : ''
        const billedTotal = Number(item.billed_total) || 0

        const isSameGroup = item.order_group_id && item.order_group_id === lastGroupId
        if (!isSameGroup) {
          totalBilledSum += billedTotal
        }
        lastGroupId = item.order_group_id

        exportRows.push([
          index + 1,
          item.customer_name || '-',
          item.merchant_name || '-',
          item.created_at ? moment(item.created_at).format('YYYY/MM/DD') : '-',
          item.invoice_number || '-',
          item.created_at ? moment(item.created_at).locale('en').format('MMMM') : '-',
          this.mapStatus(item.status),
          totalNum,
          deliveryFee,
          insuranceFee,
          isSameGroup ? '' : billedTotal,
          isSameGroup ? '' : (item.source || '-'),
        ])
      })

      // Add Sum row at the bottom
      exportRows.push([
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        totalSalesSum,
        '',
        '',
        totalBilledSum,
        '',
      ])

      // Detect consecutive order_group_id merges for SheetJS
      const merges = []
      let groupStart = 0
      for (let i = 0; i < this.transactions.length; i += 1) {
        const currentGroupId = this.transactions[i].order_group_id
        const nextGroupId = i + 1 < this.transactions.length ? this.transactions[i + 1].order_group_id : null

        if (!currentGroupId || currentGroupId !== nextGroupId) {
          const groupEnd = i
          if (currentGroupId && groupEnd > groupStart) {
            // SheetJS 0-based row indexing: row 0 is header, data[0] is row 1
            const startR = groupStart + 1
            const endR = groupEnd + 1
            merges.push({ s: { r: startR, c: 10 }, e: { r: endR, c: 10 } })
            merges.push({ s: { r: startR, c: 11 }, e: { r: endR, c: 11 } })
          }
          groupStart = i + 1
        }
      }

      const worksheet = XLSX.utils.aoa_to_sheet(exportRows)
      worksheet['!cols'] = [
        { width: 8 }, // No.
        { width: 24 }, // Customer Name
        { width: 24 }, // Merchant
        { width: 14 }, // Invoice Date
        { width: 28 }, // Invoice Number
        { width: 14 }, // Period
        { width: 20 }, // Transaction Status
        { width: 20 }, // Total Sales Amount
        { width: 16 }, // Delivery Fee
        { width: 16 }, // Insurance Fee
        { width: 18 }, // Billed Total
        { width: 14 }, // Source
      ]
      if (merges.length > 0) {
        worksheet['!merges'] = merges
      }

      // Format numeric cells and date cells
      for (let r = 1; r <= this.transactions.length; r += 1) {
        const item = this.transactions[r - 1]

        // Format Invoice Date (Column D, index 3) as pure date serial without time component
        if (item && item.created_at) {
          const m = moment(item.created_at)
          const dateCellRef = XLSX.utils.encode_cell({ r, c: 3 })
          const utcDate = Date.UTC(m.year(), m.month(), m.date())
          const epoch = Date.UTC(1899, 11, 30)
          const dateSerial = Math.round((utcDate - epoch) / (24 * 60 * 60 * 1000))
          worksheet[dateCellRef] = {
            t: 'n',
            v: dateSerial,
            z: 'd/m/yyyy',
          }
        }

        // Numeric columns: No. (0), Total Sales Amount (7), Delivery Fee (8), Insurance Fee (9), Billed Total (10)
        [0, 7, 8, 9, 10].forEach(colIndex => {
          const cellRef = XLSX.utils.encode_cell({ r, c: colIndex })
          if (worksheet[cellRef] && worksheet[cellRef].t === 'n') {
            worksheet[cellRef].z = '#,##0'
          }
        })
      }

      // Add native Excel formulas for the bottom sum row
      const sumRowIndex = this.transactions.length + 1
      const lastDataRowExcel = this.transactions.length + 1

      const salesCellRef = XLSX.utils.encode_cell({ r: sumRowIndex, c: 7 })
      worksheet[salesCellRef] = {
        t: 'n',
        f: `SUM(H2:H${lastDataRowExcel})`,
        v: totalSalesSum,
        z: '#,##0',
      }

      const billedCellRef = XLSX.utils.encode_cell({ r: sumRowIndex, c: 10 })
      worksheet[billedCellRef] = {
        t: 'n',
        f: `SUM(K2:K${lastDataRowExcel})`,
        v: totalBilledSum,
        z: '#,##0',
      }

      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions')

      const filename = `Transactions_${this.currentStartDate}_to_${this.currentEndDate}.xlsx`
      XLSX.writeFile(workbook, filename)
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

// Turn off browser overscroll/rubber-banding bounce
html,
body {
  overscroll-behavior: none !important;
  overscroll-behavior-y: none !important;
  overscroll-behavior-x: none !important;
  overflow: hidden !important;
}

#app,
.transaction-immersive-page,
.preview-spreadsheet-container,
.spreadsheet-preview-wrapper,
.x-spreadsheet,
.x-spreadsheet-sheet,
.x-spreadsheet-overlayer {
  overscroll-behavior: none !important;
  overscroll-behavior-y: none !important;
  overscroll-behavior-x: none !important;
}

.transaction-immersive-page {
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: #fff !important;
  color: #5e5873 !important;
  color-scheme: light !important;

  .preview-header {
    position: relative;
    z-index: 2000;
    flex-shrink: 0;
    min-height: 58px;
    background-color: #ffffff !important;
    border-color: #ebe9f1 !important;

    .page-title {
      font-size: 1.25rem;
      font-weight: 500;
      color: #5e5873;
    }

    .filter-label {
      font-size: 0.857rem;
      font-weight: 500;
      color: #6e6b7b;
      margin-bottom: 0.25rem;
    }
  }

  .preview-spreadsheet-container {
    position: relative;
    z-index: 1;
    flex: 1 1 0%;
    min-height: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  // Ensure v-select dropdown in this page stays strictly in light mode with high z-index and clickability
  .v-select {
    .vs__dropdown-toggle {
      background-color: #ffffff !important;
      border-color: #d8d6de !important;
    }
    .vs__selected {
      color: #6e6b7b !important;
    }
    &.vs--single.vs--open .vs__selected {
      opacity: 0.8;
      color: #6e6b7b !important;
    }
    .vs__dropdown-menu {
      top: calc(100% + 4px) !important;
      margin-top: 0 !important;
      z-index: 3000 !important;
      pointer-events: auto !important;
      background-color: #ffffff !important;
      box-shadow: 0 4px 25px 0 rgba(0, 0, 0, 0.1) !important;
    }
    .vs__fade-enter-active,
    .vs__fade-leave-active {
      pointer-events: auto !important;
    }
    .vs__dropdown-option {
      color: #6e6b7b;

      &--highlight,
      &--selected {
        background-color: #7367f0 !important;
        color: #ffffff !important;
      }
    }
  }
}
</style>
